import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function AbatementDesignPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const whatWeProvide = [
    {
      icon: 'ri-file-list-3-line',
      title: 'Project Specifications',
      description: 'Detailed abatement plans, work procedures, and technical specifications for contractors.',
    },
    {
      icon: 'ri-auction-line',
      title: 'Contractor Bid Review',
      description: 'Assist with contractor selection, bid evaluation, and scope verification.',
    },
    {
      icon: 'ri-eye-line',
      title: 'On-Site Monitoring',
      description: 'Third-party oversight during abatement to ensure compliance and quality.',
    },
    {
      icon: 'ri-checkbox-circle-line',
      title: 'Clearance Testing',
      description: 'Final air and surface testing to verify successful abatement and safe re-occupancy.',
    },
  ];

  const whoNeeds = [
    {
      title: 'Property Owners',
      description: 'Ensure safe, compliant, and cost-effective hazard removal for your buildings.',
    },
    {
      title: 'General Contractors',
      description: 'Third-party design and oversight to keep projects on schedule and compliant.',
    },
    {
      title: 'Schools & Institutions',
      description: 'AHERA-compliant abatement design and management for occupied facilities.',
    },
    {
      title: 'Housing Portfolios',
      description: 'Standardized abatement programs for multi-site lead and asbestos removal.',
    },
    {
      title: 'Developers & Investors',
      description: 'Due diligence and abatement planning for acquisitions and redevelopment.',
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Review Survey Results',
      description: 'We analyze inspection reports to understand the scope and complexity of hazards.',
    },
    {
      step: '02',
      title: 'Design Abatement Plan',
      description: 'Prepare detailed specifications, work procedures, and safety protocols for contractors.',
    },
    {
      step: '03',
      title: 'Contractor Selection',
      description: 'Assist with bid review, contractor qualifications, and scope verification.',
    },
    {
      step: '04',
      title: 'Oversight & Clearance',
      description: 'On-site monitoring during work and final clearance testing to verify success.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Abatement Design & Management
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              When hazards are found, we design the fix and manage the process. LCA prepares abatement plans and specifications, assists with contractor selection and bidding, and provides third-party oversight and clearance testing so projects are safe, compliant, and cost-effective.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-[#f59e0b] text-white px-10 py-4 rounded-lg text-base font-semibold hover:bg-[#d97706] transition-colors whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer"
            >
              Start Your Project
            </Link>
          </div>
        </div>
      </section>

      {/* What We Provide */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">What We Provide</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From design through clearance, we manage every phase of the abatement process.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whatWeProvide.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                  <i className={`${item.icon} text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Needs This */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Who Needs Abatement Design & Management?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Third-party oversight ensures abatement is done right—protecting your investment and your people.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whoNeeds.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-shadow duration-300"
              >
                <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Our Abatement Management Process</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We guide you from survey results to final clearance—no surprises, no shortcuts.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-8 border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300 h-full">
                  <div className="text-5xl font-bold text-[#f59e0b]/20 mb-4">{item.step}</div>
                  <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                  <p className="text-base text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types of Abatement */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Types of Abatement We Manage</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <div className="w-12 h-12 bg-[#f59e0b] text-white rounded-lg flex items-center justify-center mb-4">
                <i className="ri-paint-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-3">Lead-Based Paint Removal</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Encapsulation, enclosure, or full removal with EPA-compliant work practices and clearance testing.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <div className="w-12 h-12 bg-[#f59e0b] text-white rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-3">Asbestos Abatement</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Removal, encapsulation, or repair of asbestos-containing materials with air monitoring and clearance.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <div className="w-12 h-12 bg-[#f59e0b] text-white rounded-lg flex items-center justify-center mb-4">
                <i className="ri-leaf-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-3">Mold Remediation</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Containment, removal, and moisture control with post-remediation verification testing.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <div className="w-12 h-12 bg-[#f59e0b] text-white rounded-lg flex items-center justify-center mb-4">
                <i className="ri-recycle-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-3">Universal Waste Management</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                PCB ballasts, mercury-containing devices, and other regulated materials requiring special handling.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <div className="w-12 h-12 bg-[#f59e0b] text-white rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-gear-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-3">Multi-Site Programs</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Standardized abatement protocols for housing portfolios, military bases, and school districts.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <div className="w-12 h-12 bg-[#f59e0b] text-white rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-3">Emergency Response</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Rapid mobilization for water damage, fire restoration, and urgent hazard situations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose LCA */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Why Choose LCA for Abatement Management?</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl p-8 text-center border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-shield-star-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Independent Third-Party</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We work for you, not the contractor—ensuring quality, compliance, and accountability.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-money-dollar-circle-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Cost-Effective Solutions</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Clear specifications and competitive bidding help you get the best value without cutting corners.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-file-shield-2-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Defensible Documentation</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Every step is documented and traceable—for lenders, regulators, and future owners.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#f59e0b] to-[#d97706] rounded-3xl p-12 text-center shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Start Your Abatement Project?
            </h2>
            <p className="text-xl text-white/95 mb-8 leading-relaxed">
              From design through clearance, we'll help you get it done right—safely, compliantly, and on budget.
            </p>
            <Link
              to="/contact#contact-form"
              className="inline-block bg-white text-[#f59e0b] px-10 py-4 rounded-lg text-base font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer"
            >
              Talk to Our Team
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
